import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { CLINIC, NAV_LINKS, PAGE_LINKS } from "@/data/clinic";
import Logo from "./Logo";

export default function Header() {
    const [open, setOpen] = useState(false);

    return (
        <header
            data-testid="header-nav"
            className="fixed inset-x-0 top-0 z-50 border-b border-sand/60 bg-cream/80 backdrop-blur-xl"
        >
            <div className="mx-auto flex h-[88px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link to="/" aria-label="Agastya Chiro and Ancient Marma Therapy — Home">
                    <Logo />
                </Link>

                <nav className="hidden items-center gap-4 lg:flex xl:gap-6" aria-label="Primary">
                    {NAV_LINKS.map((l) => (
                        <Link
                            key={l.id}
                            to={`/#${l.id}`}
                            data-testid={`nav-${l.id}`}
                            className="whitespace-nowrap text-[12px] font-medium tracking-wide text-moss transition-colors duration-300 hover:text-forest xl:text-[13px]"
                        >
                            {l.label}
                        </Link>
                    ))}
                    {PAGE_LINKS.map((l) => (
                        <Link
                            key={l.id}
                            to={l.to}
                            data-testid={`nav-${l.id}`}
                            className="whitespace-nowrap text-[12px] font-medium tracking-wide text-moss transition-colors duration-300 hover:text-forest xl:text-[13px]"
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="hidden items-center gap-3 lg:flex">
                    <a
                        href={CLINIC.phoneTel}
                        data-testid="header-call-btn"
                        className="hidden items-center gap-2 whitespace-nowrap rounded-full border border-forest px-4 py-2.5 text-sm font-semibold text-forest transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-forest hover:text-cream xl:inline-flex"
                    >
                        <Phone className="h-4 w-4" /> Call Now
                    </a>
                    <a
                        href={CLINIC.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="header-book-btn"
                        className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-forest px-4 py-2.5 text-sm font-semibold text-cream transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-forest-deep xl:px-5"
                    >
                        Book a Consultation
                    </a>
                </div>

                <button
                    data-testid="mobile-menu-btn"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Close menu" : "Open menu"}
                    className="rounded-full border border-sand p-2.5 text-ink lg:hidden"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-sand/60 bg-cream/95 backdrop-blur-xl lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <nav className="flex flex-col gap-1 px-4 py-4" aria-label="Mobile">
                            {NAV_LINKS.map((l) => (
                                <Link
                                    key={l.id}
                                    to={`/#${l.id}`}
                                    data-testid={`mobile-nav-${l.id}`}
                                    onClick={() => setOpen(false)}
                                    className="rounded-xl px-3 py-3 font-serif text-xl text-ink transition-colors duration-300 hover:bg-bone"
                                >
                                    {l.label}
                                </Link>
                            ))}
                            {PAGE_LINKS.map((l) => (
                                <Link
                                    key={l.id}
                                    to={l.to}
                                    data-testid={`mobile-nav-${l.id}`}
                                    onClick={() => setOpen(false)}
                                    className="rounded-xl px-3 py-3 font-serif text-xl text-ink transition-colors duration-300 hover:bg-bone"
                                >
                                    {l.label}
                                </Link>
                            ))}
                            <div className="mt-3 flex gap-3 px-3 pb-2">
                                <a
                                    href={CLINIC.phoneTel}
                                    data-testid="mobile-call-btn"
                                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-forest px-4 py-3 text-sm font-semibold text-forest"
                                >
                                    <Phone className="h-4 w-4" /> Call Now
                                </a>
                                <a
                                    href={CLINIC.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="mobile-book-btn"
                                    className="inline-flex flex-1 items-center justify-center rounded-full bg-forest px-4 py-3 text-sm font-semibold text-cream"
                                >
                                    Book a Consultation
                                </a>
                            </div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
